import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import LocalStorageService, { LocalStorageKeys } from '../../../services/localStorageService';
import ErrorService from '../../../services/errorService';
import { AuthState, User } from './types';
import { login, signup, verifyOtp, forgotPassword, resetPassword, resendOtp, checkAuth, resetSubAdminPassword } from './actions';
import { updateAdminProfile } from '../accountSettings/actions';

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: 'idle',
  error: null,
  message: null,
  tempPassword: null,
};

const resetAuthState = (state: AuthState) => {
  state.isAuthenticated = false;
  state.user = null;
  state.tempPassword = null;

  LocalStorageService.remove(LocalStorageKeys.ACCESS_TOKEN);
  LocalStorageService.remove(LocalStorageKeys.REFRESH_TOKEN);
  LocalStorageService.remove(LocalStorageKeys.USER);
  LocalStorageService.remove(LocalStorageKeys.KEEP_ME_SIGNED_IN);
  sessionStorage.removeItem('session-active');
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logoutSuccess(state) {
      resetAuthState(state);
    },

    authSuccess(state, action: PayloadAction<{ user: User }>) {
      state.isAuthenticated = true;
      state.user = action.payload.user;
    },
    clearMessage(state) {
        state.message = null;
    },
    setTempPassword(state, action: PayloadAction<string>) {
        state.tempPassword = action.payload;
    },
    clearError(state) {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
        state.message = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = 'succeeded';
        state.message = action.payload.message;
        LocalStorageService.set<boolean>(LocalStorageKeys.IS_LOGIN_PENDING, true);
      })
      .addCase(login.rejected, (state, action: PayloadAction<any>) => {
        state.loading = 'failed';
        state.error = ErrorService.getErrorMessage(action.payload);
      })

      .addCase(signup.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
        state.message = null;
      })
      .addCase(signup.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = 'succeeded';
        state.message = action.payload.message;
      })
      .addCase(signup.rejected, (state, action: PayloadAction<any>) => {
        state.loading = 'failed';
        state.error = ErrorService.getErrorMessage(action.payload);
      })

      .addCase(verifyOtp.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
        state.message = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = 'succeeded';
        state.isAuthenticated = true;
        state.user = action.payload.data.user;
      })
      .addCase(verifyOtp.rejected, (state, action: PayloadAction<any>) => {
        state.loading = 'failed';
        state.error = ErrorService.getErrorMessage(action.payload);
      })

      .addCase(forgotPassword.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
        state.message = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = 'succeeded';
        state.message = action.payload.message;
      })
      .addCase(forgotPassword.rejected, (state, action: PayloadAction<any>) => {
        state.loading = 'failed';
        state.error = ErrorService.getErrorMessage(action.payload);
      })

      .addCase(resetPassword.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
        state.message = null;
      })
      .addCase(resetPassword.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = 'succeeded';
        state.message = action.payload.message;
      })
      .addCase(resetPassword.rejected, (state, action: PayloadAction<any>) => {
        state.loading = 'failed';
        state.error = ErrorService.getErrorMessage(action.payload);
      })

      .addCase(resendOtp.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
        state.message = null;
      })
      .addCase(resendOtp.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = 'succeeded';
        state.message = action.payload.message;
      })
      .addCase(resendOtp.rejected, (state, action: PayloadAction<any>) => {
        state.loading = 'failed';
        state.error = ErrorService.getErrorMessage(action.payload);
      })

      .addCase(resetSubAdminPassword.pending, (state) => {
        state.loading = 'pending';
        state.error = null;
        state.message = null;
      })
      .addCase(resetSubAdminPassword.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = 'succeeded';
        state.message = action.payload.message;
      })
      .addCase(resetSubAdminPassword.rejected, (state, action: PayloadAction<any>) => {
        state.loading = 'failed';
        state.error = ErrorService.getErrorMessage(action.payload);
      })

      .addCase(updateAdminProfile.fulfilled, (state, action) => {
        if (state.user) {
          state.user.full_name = action.payload.data.user_full_name;
          state.user.username = action.payload.data.user_username;
          state.user.user_bio = action.payload.data.user_bio;
          state.user.profile_image = action.payload.data.user_profile_image;
        }
      })

      .addCase(checkAuth.fulfilled, (state, action: PayloadAction<{ user: User | null, logout: boolean }>) => {
        if (action.payload.user) {
            state.isAuthenticated = true;
            state.user = action.payload.user;
        } else if (action.payload.logout) {
            resetAuthState(state);
        }
      });
  },
});

export const { logoutSuccess, authSuccess, clearMessage, setTempPassword, clearError } = authSlice.actions;

export default authSlice.reducer;
